import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export const COLORS = ["#34D399", "#38BDF8", "#FBBF24", "#E879F9", "#F87171", "#A78BFA", "#22D3EE", "#FB923C"];
export const money = (v, cur) => (v == null ? "-" : `${v < 0 ? "-" : ""}${Math.abs(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${cur ? ` ${cur}` : ""}`);
const label = (b) => (b.symbol ? `${b.symbol} ${b.period}` : b.name);
export const tip = { contentStyle: { background: "#0B1220", border: "1px solid #1E3A8A", borderRadius: 8, fontSize: 12 }, itemStyle: { color: "#E2E8F0" } };

function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded-xl bg-[#050914] border border-blue-900/40 p-4" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

export function PerfCharts({ data }) {
  const { bots, currency: cur } = data;
  const best = bots[0];
  const bars = bots.map((b) => ({ name: label(b), full: b.name, net: b.net }));
  const pie = bots.filter((b) => b.net > 0).map((b) => ({ name: label(b), value: b.net }));
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <Stat testid="perf-total" label={`Net profit${data.total_gross != null ? ` · gross ${money(data.total_gross, cur)}` : ""}`} value={money(data.total_net, cur)} tone={data.total_net >= 0 ? "text-[#34D399]" : "text-[#F87171]"} />
        <Stat testid="perf-best" label="Top result this period" value={best && best.trades ? best.name : "-"} tone="text-white text-base" />
        <Stat testid="perf-reporting" label="Bots reporting" value={`${data.reporting} of ${bots.length}`} tone="text-[#38BDF8]" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
        <div className="lg:col-span-3 rounded-xl bg-[#050914] border border-blue-900/40 p-4" data-testid="perf-bar-chart">
          <div className="text-sm font-semibold mb-2">Profit per bot</div>
          <ResponsiveContainer width="100%" height={Math.max(220, bars.length * 48)}>
            <BarChart data={bars} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" stroke="#64748B" fontSize={11} />
              <YAxis type="category" dataKey="name" width={92} stroke="#94A3B8" fontSize={11} />
              <Tooltip {...tip} formatter={(v) => money(v, cur)} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} cursor={{ fill: "#1E293B55" }} />
              <Bar dataKey="net" radius={[0, 6, 6, 0]}>{bars.map((b) => <Cell key={b.name} fill={b.net >= 0 ? "#34D399" : "#F87171"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-2 rounded-xl bg-[#050914] border border-blue-900/40 p-4" data-testid="perf-pie-chart">
          <div className="text-sm font-semibold mb-2">Share of profit</div>
          {pie.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} stroke="none">
                  {pie.map((p, i) => <Cell key={p.name} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip {...tip} formatter={(v) => money(v, cur)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-xs text-[#64748B] py-16 text-center">No profitable bots in this period yet.</div>}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#94A3B8]">
            {pie.map((p, i) => <span key={p.name} className="flex items-center gap-1"><i className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p.name}</span>)}
          </div>
        </div>
      </div>
    </>
  );
}

export function PerfTable({ data }) {
  const cur = data.currency;
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-blue-900/40" data-testid="perf-league-table">
      <table className="w-full text-sm">
        <thead className="mono text-[10px] uppercase tracking-widest text-[#64748B] bg-[#050914]">
          <tr>{["#", "Bot", "Net profit", "Gross profit", "Swap + comm.", "Trades", "Win rate", "Profit factor", "Open now", "Last report"].map((h) => <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>
          {data.bots.map((b, i) => (
            <tr key={b.store_id} data-testid={`perf-row-${b.store_id}`} className="border-t border-blue-900/30">
              <td className="px-3 py-3 text-[#FBBF24] font-semibold">{i + 1}</td>
              <td className="px-3 py-3 whitespace-nowrap">{b.name}</td>
              <td className={`px-4 py-3 font-semibold whitespace-nowrap ${b.net >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`}>{money(b.net, cur)}</td>
              <td className="px-3 py-3 whitespace-nowrap" data-testid={`perf-gross-${b.store_id}`}>{money(b.gross, cur)}</td>
              <td className="px-3 py-3 whitespace-nowrap text-[#94A3B8]" data-testid={`perf-costs-${b.store_id}`}>{money(b.costs, cur)}</td>
              <td className="px-3 py-3">{b.trades}</td>
              <td className="px-3 py-3">{b.win_rate == null ? "-" : `${b.win_rate}%`}</td>
              <td className="px-3 py-3">{b.profit_factor ?? (b.no_losses ? "no losses" : "-")}</td>
              <td className="px-3 py-3 whitespace-nowrap">{money(b.open, cur)}</td>
              <td className="px-3 py-3 text-xs text-[#94A3B8] whitespace-nowrap">{b.last_report ? new Date(b.last_report).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "not yet"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-3 text-[11px] text-[#64748B] border-t border-blue-900/30" data-testid="perf-reconcile-note">
        Gross profit matches the Profit column in MT5's History tab. Net profit = gross + swap + commission (both entry and exit).
        Periods use your broker's server clock, the same as MT5. Closed trades show up within about a minute while MT5 is running.
      </div>
    </div>
  );
}

export function PerfTimeline({ data }) {
  const { bots, timeline, currency: cur } = data;
  if (!timeline?.length) return null;
  return (
    <div className="mt-4 rounded-xl bg-[#050914] border border-blue-900/40 p-4" data-testid="perf-timeline-chart">
      <div className="text-sm font-semibold mb-2">Profit over time (running total)</div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={timeline}>
          <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
          <XAxis dataKey="d" stroke="#64748B" fontSize={10} />
          <YAxis stroke="#64748B" fontSize={10} width={70} tickFormatter={(v) => money(v, cur)} />
          <Tooltip {...tip} formatter={(v) => money(v, cur)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="stepAfter" dataKey="total" name="All bots" stroke="#F8FAFC" strokeWidth={3} dot={false} />
          {bots.map((b, i) => <Line key={b.store_id} type="stepAfter" dataKey={b.store_id} name={label(b)} stroke={COLORS[(i + 1) % COLORS.length]} strokeWidth={1.5} dot={false} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
